import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface DocumentTemplate {
  templateKey: string;
  countryCode: string;
  locale: string;
  category?: string;
  title: string;
  variables: string[];
}

/** AI-assisted variable fill: proposes values from the employee/company record + template. */
export async function aiFillDocument(input: { templateKey: string; employeeId?: string }) {
  const r = await api.post<{ bindings: Record<string, string>; aiUsed: boolean }>('/api/documents/ai-fill', input);
  return r.data;
}

export interface DocumentRecord {
  id: string;
  employeeId: string | null;
  templateKey: string | null;
  title: string;
  status: 'draft' | 'sent' | 'signed' | 'filed' | 'archived' | 'rejected';
  renderedMd: string | null;
  hasFile?: boolean;
  signatures: Array<{ signer_user_id: string; signer_name: string; signed_at: string; ip: string | null; document_hash: string }>;
  signedAt: string | null;
  createdAt: string;
}

/** Authenticated (cookie) URL to view/download a document's stored file. */
export function documentFileUrl(id: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';
  return `${base}/api/documents/${id}/file`;
}

export function useDocumentTemplates() {
  return useQuery({
    queryKey: ['documents', 'templates'],
    queryFn: async () => {
      const r = await api.get<{ templates: DocumentTemplate[] }>('/api/documents/templates');
      return r.data.templates;
    },
    staleTime: 5 * 60_000,
  });
}

export function useDocuments(params: { employeeId?: string } = {}) {
  return useQuery({
    queryKey: ['documents', 'list', params],
    queryFn: async () => {
      const r = await api.get<{ documents: DocumentRecord[] }>('/api/documents', { params });
      return r.data.documents;
    },
  });
}

export function useGenerateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateKey: string; employeeId?: string; title?: string; bindings: Record<string, string> }) => {
      const r = await api.post<DocumentRecord>('/api/documents/generate', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useSendDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<DocumentRecord>(`/api/documents/${id}/send`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

/**
 * Edit a draft document — title and rendered markdown body. Locked
 * once the document is sent or signed (server returns 400 with
 * "Document is sent and can no longer be edited.").
 */
export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; title?: string; renderedMd?: string }) => {
      const { id, ...rest } = input;
      const r = await api.patch<DocumentRecord>(`/api/documents/${id}`, rest);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useSignDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<DocumentRecord>(`/api/documents/${id}/sign`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; title?: string; employeeId?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.title) fd.append('title', input.title);
      if (input.employeeId) fd.append('employeeId', input.employeeId);
      const r = await api.post<DocumentRecord>('/api/documents/upload', fd);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/documents/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}
